/**
 * Loyalty Program API Service
 * Base URL: REACT_APP_API_ENDPOINT_LOYALTY (localhost:4004)
 * All endpoints: /api/v1/loyalty/program/*
 */

import { LOYALTY_API } from "../redux/api";

// ─── POST /loyalty/program ─────────────────────────────────────
export interface CreateProgramPayload {
  name: string;
  points_name: string;
  accrual_type: 'spend' | 'visit' | 'item_category';
  points_per_unit: number;
  minimum_transaction_amount: number;
  rounding_mode: 'floor' | 'ceil' | 'round';
  granttotal_basis?: 'before-discounts' | 'after-discounts';
  value_per_point: number;
  points_required: number;
  point_expiration?: {
    enabled: boolean;
    expiration_type: 'inactive' | 'fixed';
    expiration_days: number;

    notify_before_days: number;
  };
}

export const createProgram = (payload: CreateProgramPayload) => {
  return LOYALTY_API({
    method: "post",
    url: `/api/v1/loyalty/program`,
    data: payload,
    headers: { "Content-Type": "application/json" },
  });
};

// ─── GET /loyalty/program ──────────────────────────────────────
export const getProgram = () => {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/program`,
  });
};

// ─── PUT /loyalty/program ──────────────────────────────────────
export interface UpdateProgramPayload {
  name?: string;
  points_name?: string;
  accrual_type?: 'spend' | 'visit' | 'item_category';
  points_per_unit?: number;
  minimum_transaction_amount?: number;
  rounding_mode?: 'floor' | 'ceil' | 'round';
  granttotal_basis?: 'before-discounts' | 'after-discounts';
  sign_up_bonus_enabled?: boolean;
  sign_up_bonus_points?: number;
  // DISABLED: customer_at_risk_days?: number | null;
  // DISABLED: customer_lapsed_days?: number | null;
  point_expiration?: {
    enabled: boolean;
    expiration_type: 'inactive' | 'fixed';
    expiration_days: number;

    notify_before_days: number;
  };
  locations_summary?: {
    locations: Array<{ location_id: string; accrual_enabled: boolean }>;
  };
}

export const updateProgram = (payload: UpdateProgramPayload) => {
  return LOYALTY_API({
    method: "put",
    url: `/api/v1/loyalty/program`,
    data: payload,
    headers: { "Content-Type": "application/json" },
  });
};

// ─── PATCH /loyalty/program/status ────────────────────────────
export const updateProgramStatus = (action: 'activate' | 'pause' | 'resume') => {
  return LOYALTY_API({
    method: "patch",
    url: `/api/v1/loyalty/program/status`,
    data: { action },
    headers: { "Content-Type": "application/json" },
  });
};

// ─── GET /loyalty/program/special-rewards ─────────────────────
export const getSpecialRewards = () => {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/program/special-rewards`,
  });
};

// ─── PUT /loyalty/program/special-rewards ─────────────────────
export interface UpdateSpecialRewardsPayload {
  sign_up_bonus_enabled: boolean;
  sign_up_bonus_points: number;
}

export const updateSpecialRewards = (payload: UpdateSpecialRewardsPayload) => {
  return LOYALTY_API({
    method: "put",
    url: `/api/v1/loyalty/program/special-rewards`,
    data: payload,
    headers: { "Content-Type": "application/json" },
  });
};

// ─── GET /loyalty/dashboard ───────────────────────────────────
export interface GetDashboardParams {
  period: string;
  location_id?: string;
}

export const getDashboard = (params: GetDashboardParams) => {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/dashboard`,
    params,
  });
};

// ─── GET /loyalty/program/media ───────────────────────────────
export interface LoyaltyMediaIcon {
  id: string;
  file_name: string;
  mime_type: string;
  tag: string;
  sort_order: number;
  url: string;
  created_at: string;
}

export const getLoyaltyMediaIcons = () => {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/program/media`,
  });
};

// ─── GET /loyalty/notifications ───────────────────────────────
export const getNotificationConfigs = () => {
  return LOYALTY_API({
    method: "get",
    url: `/api/v1/loyalty/notifications`,
  });
};

// ─── PUT /loyalty/notifications/:configId ─────────────────────
// Backend accepts only { enabled } — channels and templates are managed server-side
export interface UpdateNotificationPayload {
  enabled: boolean;
}

export const updateNotificationConfig = (configId: string, payload: UpdateNotificationPayload) => {
  return LOYALTY_API({
    method: "put",
    url: `/api/v1/loyalty/notifications/${configId}`,
    data: payload,
    headers: { "Content-Type": "application/json" },
  });
};
